import { useCallback, useEffect, useMemo, useState, useRef } from 'react'
import Draggable from 'react-draggable'
import { useDispatch, useSelector } from 'react-redux'
import { Tooltip, notification } from 'antd'
import * as AntIcons from '@ant-design/icons'
import PropTypes from 'prop-types'

import { sizes } from '@qflow/theme'
import {
  Container,
  InnerContainer,
  ActionStripCard,
  ActionStripButtonContainer,
  ActionStripButtonGroup
} from 'portal/components/CommonStyles/ActionStripStyledComponents'

import {
  icons,
  Clickable,
  Text,
  Padding,
  ColourButton,
  Spacer,
  Spinner
} from 'portal/lib/primitives'
import {
  assignRecords,
  unassignRecords,
  tagRecords,
  untagRecords
} from 'portal/store/events/actions'
import { useModuleIds, useUserProjectRole } from 'portal/lib/hooks'
import { trackMixpanel } from 'portal/store/mixpanel/actions'

import { AssignmentStrip } from 'portal/lib/sharedComponents/AssignmentStrip'
import { UnassignmentStrip } from 'portal/lib/sharedComponents/UnassignmentStrip'
import { TaggingStrip } from 'portal/lib/sharedComponents/TaggingStrip'
import { UntaggingStrip } from 'portal/lib/sharedComponents/UntaggingStrip'
import { AssignmentLongRunningOperationModal } from 'portal/lib/sharedComponents/AssignmentLongRunningOperationModal'
import { RecordUpdateSection } from './RecordUpdateSection'

export function ActionStrip({
  recordIds,
  recordAssigneeMapping,
  recordTagsMapping,
  recordDataMapping,
  selectedRecordsContainUnloaded,
  onClose,
  onApply
}) {
  const LONG_RUNNING_WARNING_THRESHOLD = 1000
  const MAX_ASSIGNMENT_LIMIT = 10000
  const MAX_UPDATE = 100
  const MAX_TAG_RECORDS = 100

  const dispatch = useDispatch()
  const { accountId, projectId, moduleName } = useModuleIds()
  const [showAssignmentStrip, setShowAssignmentStrip] = useState(true)
  const [showUnassignmentStrip, setShowUnassignmentStrip] = useState(false)
  const [showUpdateSection, setShowUpdateSection] = useState(false)
  const [showTaggingStrip, setShowTaggingStrip] = useState(false)
  const [showUntaggingStrip, setShowUntaggingStrip] = useState(false)
  const [showLongRunningOperationModal, setShowLongRunningOperationModal] =
    useState(false)

  const [reloadCount, setReloadCount] = useState(0)
  const { isProjectAdmin } = useUserProjectRole(accountId, projectId)
  const draggableRef = useRef(null)

  const projectUsers = useSelector(state => state.projectUsers)
  const assign = useSelector(state => state.events.assign)
  const unassign = useSelector(state => state.events.unassign)
  const dismissal = useSelector(state => state.events.dismissal)
  const tag = useSelector(state => state.events.tag)
  const untag = useSelector(state => state.events.untag)

  // Use selected Records to create a set of unique users who are assigned to 1 or more events/records
  const relatedAssignees = useMemo(() => {
    const assignedUsers = {}
    recordIds.forEach(record => {
      if (recordAssigneeMapping[record]) {
        recordAssigneeMapping[record].forEach(assignee => {
          assignedUsers[assignee.userId] = {
            userId: assignee.userId,
            displayName: assignee.userName,
            email: projectUsers.users[`${projectId}_${assignee.userId}`]?.email
          }
        })
      }
    })
    return assignedUsers
  }, [projectId, projectUsers.users, recordAssigneeMapping, recordIds])

  const hasRelatedAssignees = Object.keys(relatedAssignees).length > 0

  const handleAssignBtnClick = () => {
    setShowUnassignmentStrip(false)
    setShowUpdateSection(false)
    setShowTaggingStrip(false)
    setShowUntaggingStrip(false)
    setShowAssignmentStrip(!showAssignmentStrip)
  }

  const handleUnassignBtnClick = () => {
    setShowAssignmentStrip(false)
    setShowUpdateSection(false)
    setShowTaggingStrip(false)
    setShowUntaggingStrip(false)
    setShowUnassignmentStrip(!showUnassignmentStrip)
  }

  const handleUpdateBtnClick = () => {
    setShowAssignmentStrip(false)
    setShowUnassignmentStrip(false)
    setShowTaggingStrip(false)
    setShowUntaggingStrip(false)
    setShowUpdateSection(!showUpdateSection)
  }

  const handleTagBtnClick = () => {
    setShowAssignmentStrip(false)
    setShowUnassignmentStrip(false)
    setShowUpdateSection(false)
    setShowUntaggingStrip(false)
    setShowTaggingStrip(!showTaggingStrip)
  }

  const handleUntagBtnClick = () => {
    setShowAssignmentStrip(false)
    setShowUnassignmentStrip(false)
    setShowUpdateSection(false)
    setShowTaggingStrip(false)
    setShowUntaggingStrip(!showUntaggingStrip)
  }

  const triggerReload = useCallback(() => {
    const newReloadCount = reloadCount + 1
    onApply('reloadCount', newReloadCount)
    setReloadCount(newReloadCount)
  }, [onApply, reloadCount])

  const handleRecordsAssign = useCallback(
    assignTo => {
      dispatch(
        assignRecords(accountId, projectId, recordIds, assignTo, () => {
          setTimeout(() => {
            onClose?.(true)
            setShowLongRunningOperationModal(false)
            triggerReload()
          }, 500)
        })
      )
      setShowAssignmentStrip(false)
      if (recordIds.length * assignTo.length > LONG_RUNNING_WARNING_THRESHOLD) {
        setShowLongRunningOperationModal(true)
      }
    },
    [accountId, dispatch, onClose, projectId, recordIds, triggerReload]
  )

  const handleRecordsUnassign = useCallback(
    unassignUsers => {
      dispatch(
        unassignRecords(accountId, projectId, recordIds, unassignUsers, () => {
          setTimeout(() => {
            onClose?.(true)
            setShowLongRunningOperationModal(false)
            triggerReload()
          }, 500)
        })
      )
      setShowUnassignmentStrip(false)
      if (
        recordIds.length * unassignUsers.length >
        LONG_RUNNING_WARNING_THRESHOLD
      ) {
        setShowLongRunningOperationModal(true)
      }
    },
    [accountId, dispatch, onClose, projectId, recordIds, triggerReload]
  )

  const handleRecordsTag = useCallback(
    tags => {
      dispatch(
        tagRecords(
          accountId,
          projectId,
          recordIds,
          tags,
          () => {
            setTimeout(() => {
              onClose?.(true)
              triggerReload()
            }, 500)
          },
          () => {
            notification.error({
              message: 'Unable to tag records',
              description:
                'A conflict occurred while updating records. Please refresh and try again.'
            })
          }
        )
      )
      dispatch(
        trackMixpanel('frontend_record_tags_added', {
          project_id: projectId,
          account_id: accountId,
          record_count: recordIds.length,
          tag_count: tags.length,
          record_type: moduleName
        })
      )
      setShowTaggingStrip(false)
    },
    [
      accountId,
      dispatch,
      onClose,
      projectId,
      recordIds,
      triggerReload,
      moduleName
    ]
  )

  const handleRecordsUntag = useCallback(
    tags => {
      dispatch(
        untagRecords(
          accountId,
          projectId,
          recordIds,
          tags,
          () => {
            setTimeout(() => {
              onClose?.(true)
              triggerReload()
            }, 500)
          },
          () => {
            notification.error({
              message: 'Unable to untag records',
              description:
                'A conflict occurred while updating records. Please refresh and try again.'
            })
          }
        )
      )
      dispatch(
        trackMixpanel('frontend_record_tags_removed', {
          project_id: projectId,
          account_id: accountId,
          record_count: recordIds.length,
          tag_count: tags.length,
          record_type: moduleName
        })
      )
      setShowUntaggingStrip(false)
    },
    [
      accountId,
      dispatch,
      onClose,
      projectId,
      recordIds,
      triggerReload,
      moduleName
    ]
  )

  const working =
    dismissal.loading ||
    assign.loading ||
    unassign.loading ||
    tag.loading ||
    untag.loading
  const visible = recordIds?.length > 0
  const showUnassignBtn = hasRelatedAssignees || selectedRecordsContainUnloaded
  const tagLimitExceeded = recordIds?.length > MAX_TAG_RECORDS

  const isWasteModule = moduleName === 'waste'
  const isDeliveryModule = moduleName === 'delivery'
  const showUpdateButton = isWasteModule || isDeliveryModule

  // Collect unique tags from selected records for untag suggestions
  const relatedTags = useMemo(() => {
    const tagSet = new Set()
    recordIds.forEach(record => {
      if (recordTagsMapping?.[record]) {
        recordTagsMapping[record].forEach(t => tagSet.add(t))
      }
    })
    return Array.from(tagSet).sort()
  }, [recordIds, recordTagsMapping])

  const showUntagBtn = relatedTags.length > 0

  useEffect(() => {
    if (
      showUnassignmentStrip &&
      !hasRelatedAssignees &&
      !selectedRecordsContainUnloaded
    ) {
      setShowUnassignmentStrip(false)
    }
  }, [
    hasRelatedAssignees,
    selectedRecordsContainUnloaded,
    showUnassignmentStrip
  ])

  return (
    <Container $visible={visible}>
      {working && <Spinner />}
      <Draggable bounds="body" nodeRef={draggableRef} cancel=".address-modal">
        <InnerContainer ref={draggableRef} style={{ cursor: 'move' }}>
          <ActionStripCard>
            <Padding noVertical>
              <Text bold size={sizes.MEDIUM}>
                {recordIds.length} selected
              </Text>
            </Padding>

            {isProjectAdmin && (
              <ActionStripButtonContainer>
                <ActionStripButtonGroup>
                  <ColourButton.Basic
                    type="button"
                    onClick={handleAssignBtnClick}
                    disabled={working}
                    data-testid="Assign"
                    className={showAssignmentStrip ? 'selected' : ''}
                    style={{
                      borderRadius: showUnassignBtn ? '5px 0 0 5px' : '5px'
                    }}
                  >
                    <icons.AddPerson
                      colour="currentColor"
                      size={sizes.MEDIUM}
                    />
                    <Spacer.Fine />
                    Assign
                  </ColourButton.Basic>

                  {showUnassignBtn && (
                    <ColourButton.Basic
                      type="button"
                      data-testid="Unassign"
                      onClick={handleUnassignBtnClick}
                      disabled={working}
                      className={showUnassignmentStrip ? 'selected' : ''}
                      style={{
                        borderLeft: 'none',
                        borderRadius: '0 5px 5px 0'
                      }}
                    >
                      <icons.RemovePerson
                        colour="currentColor"
                        size={sizes.MEDIUM}
                      />
                      <Spacer.Fine />
                      Unassign
                    </ColourButton.Basic>
                  )}
                </ActionStripButtonGroup>

                <ActionStripButtonGroup style={{ marginLeft: '0.5rem' }}>
                  <Tooltip
                    title={
                      tagLimitExceeded
                        ? `Tag is limited to ${MAX_TAG_RECORDS} records at a time`
                        : 'Tag records'
                    }
                    placement="top"
                  >
                    <span>
                      <ColourButton.Basic
                        type="button"
                        id="Tag"
                        onClick={handleTagBtnClick}
                        disabled={working || tagLimitExceeded}
                        className={showTaggingStrip ? 'selected' : ''}
                        style={{
                          borderRadius: showUntagBtn ? '5px 0 0 5px' : '5px'
                        }}
                      >
                        <AntIcons.TagOutlined />
                        <Spacer.Fine />
                        Tag
                      </ColourButton.Basic>
                    </span>
                  </Tooltip>

                  {showUntagBtn && (
                    <Tooltip
                      title={
                        tagLimitExceeded
                          ? `Untag is limited to ${MAX_TAG_RECORDS} records at a time`
                          : 'Remove tags'
                      }
                      placement="top"
                    >
                      <span>
                        <ColourButton.Basic
                          type="button"
                          id="Untag"
                          onClick={handleUntagBtnClick}
                          disabled={working || tagLimitExceeded}
                          className={showUntaggingStrip ? 'selected' : ''}
                          style={{
                            borderLeft: 'none',
                            borderRadius: '0 5px 5px 0'
                          }}
                        >
                          <AntIcons.TagsOutlined />
                          <Spacer.Fine />
                          Untag
                        </ColourButton.Basic>
                      </span>
                    </Tooltip>
                  )}
                </ActionStripButtonGroup>

                {showUpdateButton && (
                  <ActionStripButtonGroup style={{ marginLeft: '0.5rem' }}>
                    <Tooltip title="Update records" placement="top">
                      <ColourButton.Basic
                        type="button"
                        data-testid="Update"
                        onClick={handleUpdateBtnClick}
                        disabled={working}
                        className={showUpdateSection ? 'selected' : ''}
                        style={{
                          borderRadius: '5px'
                        }}
                      >
                        <AntIcons.SyncOutlined />
                        <Spacer.Fine />
                        Update
                      </ColourButton.Basic>
                    </Tooltip>
                  </ActionStripButtonGroup>
                )}
              </ActionStripButtonContainer>
            )}

            <Clickable
              size="small"
              title="Close"
              onClick={() => onClose(false)}
              disabled={working}
            >
              <icons.Cross size={sizes.X_SMALL} colour="inherit" />
            </Clickable>
          </ActionStripCard>

          {isProjectAdmin && showAssignmentStrip && (
            <AssignmentStrip
              users={projectUsers.users}
              onApply={handleRecordsAssign}
              recordCount={recordIds.length}
              maxAssignment={MAX_ASSIGNMENT_LIMIT}
            />
          )}

          {isProjectAdmin && showUnassignmentStrip && (
            <UnassignmentStrip
              users={
                selectedRecordsContainUnloaded
                  ? projectUsers.users
                  : relatedAssignees
              }
              onApply={handleRecordsUnassign}
              recordCount={recordIds.length}
              maxAssignment={MAX_ASSIGNMENT_LIMIT}
            />
          )}

          {isProjectAdmin && showUpdateSection && (
            <RecordUpdateSection
              onApply={() => {
                setShowUpdateSection(false)
                setTimeout(() => {
                  onClose?.(true)
                  triggerReload()
                }, 500)
              }}
              recordIds={recordIds}
              recordCount={recordIds.length}
              maxUpdate={MAX_UPDATE}
              recordDataMapping={recordDataMapping}
            />
          )}

          {isProjectAdmin && showTaggingStrip && (
            <TaggingStrip
              onApply={handleRecordsTag}
              recordCount={recordIds.length}
            />
          )}

          {isProjectAdmin && showUntaggingStrip && (
            <UntaggingStrip
              relatedTags={relatedTags}
              onApply={handleRecordsUntag}
              recordCount={recordIds.length}
            />
          )}

          {showLongRunningOperationModal && (
            <AssignmentLongRunningOperationModal />
          )}
        </InnerContainer>
      </Draggable>
    </Container>
  )
}

ActionStrip.propTypes = {
  recordIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  recordTagsMapping: PropTypes.object,
  onClose: PropTypes.func
}
